import React, { useState, useEffect, useCallback } from 'react';
import { Platform, Linking, View, Text, TouchableOpacity, TouchableWithoutFeedback, Modal, Image, StyleSheet, Dimensions, FlatList } from 'react-native';
import { useNavigation, NavigationProp } from '@react-navigation/native';
import { useAuthenticator } from '@aws-amplify/ui-react';
import Slider from '@react-native-community/slider';
import { RootStackParamList } from './App.web';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiUrl } from './utils/config';
import * as Font from 'expo-font';
import ParentalGateModal from './ParentalGateModal';


const { width: screenWidth } = Dimensions.get('window'); // Get screen width
const CONTENT_WIDTH = Math.min(screenWidth * 0.9, 400); // adjustable

const SettingsPage: React.FC = () => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const [voice, setVoice] = useState('Joanna');
  const [volume, setVolume] = useState(50);
  const [speed, setSpeed] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [showParentalGate, setShowParentalGate] = useState(false);
  const [parentalGateIntent, setParentalGateIntent] = useState<'delete' | 'subscription' | null>(null);


  const voicesArray = [
    'Matthew',
    'Amy',
    'Joanna',
  ];

  const { signOut } = useAuthenticator();

  const loadFonts = async () => {
        await Font.loadAsync({
          'Futura': require('./assets/fonts/futura.ttf'),
        });
      };


  const getUserID = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      const user_id = session?.tokens?.accessToken?.payload?.sub;
      return user_id || null;
    } catch (error) {
      console.error('Error retrieving user ID:', error);
      return null;
    }
  };

  const getAuthToken = useCallback(async (): Promise<any | null> => {
    try {
      const session = await fetchAuthSession();
      const id_token = session?.tokens?.idToken;
      return id_token || null;
    } catch (error) {
      console.error('Error retrieving auth token:', error);
      return null;
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const user_id = await getUserID();
      const authToken = await getAuthToken();
      const response = await fetch(`${apiUrl}settings/get-user-settings?user_id=${user_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await response.json();
      setVoice(data.voice || 'Joanna');
      setVolume(data.volume);
      setSpeed(data.speed || 1);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [getAuthToken, getUserID]);

  const updateSettings = async () => {
    try {
      const user_id = await getUserID();
      const authToken = await getAuthToken();
      const response = await fetch(`${apiUrl}settings/set-user-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          user_id,
          voice,
          volume,
          speed,
        }),
      });
      console.log('Settings updated successfully:', await response.json());
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleSave = async () => {
    await updateSettings();
  };

  const handleBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('StoriesPage'); // Navigate to StoriesPage explicitly
    }
  };

  const handleManageSubscription = () => {
    if (Platform.OS === 'android') {
      setParentalGateIntent('subscription');
      setShowParentalGate(true);
    } else {
      navigation.navigate('SubscriptionPage');
    }
  };

  const handleReferFriend = () => {
    navigation.navigate('ReferralPage');
  };

  useEffect(() => {
    fetchSettings();
    loadFonts();
  }, []); // Empty dependency array ensures the function runs only once

  const handleSelectVoice = (selectedVoice: string) => {
    setVoice(selectedVoice);
    setModalVisible(false); // Close modal after selection
  };

  const handleParentalGateSuccess = () => {
    if (parentalGateIntent === 'delete') {
      Linking.openURL('https://interactivebedtime.com/delete-account');
    } else if (parentalGateIntent === 'subscription') {
      navigation.navigate('SubscriptionPage');
    }
    setShowParentalGate(false);
    setParentalGateIntent(null);
  };
  
  const handleParentalGateCancel = () => {
    setShowParentalGate(false);
    setParentalGateIntent(null);
  };
  

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack} style={styles.iconButton}>
          <Image source={require('./assets/back-btn.png')} style={styles.icon} />
        </TouchableOpacity>
        <TouchableOpacity onPress={signOut} style={styles.iconButton}>
          <Image source={require('./assets/sign-out-icon.png')} style={styles.icon} />
        </TouchableOpacity>
      </View>

      <View style={styles.settingsContainer}>
        {/* Voice Setting */}
        <View style={styles.settingRow}>
          <Image source={require('./assets/voice-icon.png')} style={styles.voiceSettingIcon} />
          <View style={styles.dropdownContainer}>
        {/* Dropdown Trigger */}
        <TouchableOpacity
          style={styles.dropdown}
          onPress={() => setModalVisible(true)}
        >
          <Text style={styles.dropdownText}>
            {voice || 'Pick a Voice'}
          </Text>
        </TouchableOpacity>
      </View>
        </View>
        {/* Volume Slider with Icon */}
        <View style={styles.sliderRow}>
          <Image source={require('./assets/volume-icon.png')} style={styles.settingIcon} />
          <Slider
            style={styles.slider}
            minimumValue={0}
            maximumValue={100}
            value={volume}
            onValueChange={(value) => setVolume(value)}
            minimumTrackTintColor="#544564"
            thumbTintColor="#AFC9BF"
          />
          <Text style={styles.sliderValue}>{Math.round(volume)}%</Text>
        </View>

        {/* Speed Slider with Icon */}
        <View style={styles.sliderRow}>
          <Image source={require('./assets/speed-icon.png')} style={styles.settingIcon} />
          <Slider
            style={styles.slider}
            minimumValue={0.5}
            maximumValue={2}
            step={0.25}
            value={speed}
            onValueChange={(value) => setSpeed(value)}
            minimumTrackTintColor="#544564"
            thumbTintColor="#AFC9BF"
          />
          <Text style={styles.sliderValue}>{speed.toFixed(2)}x</Text>
        </View>

        {/* Manage Subscription Button */}
        <View style={styles.buttonContainer}>
          <View style={styles.subscriptionShadowBackground} />
          <TouchableOpacity onPress={handleManageSubscription} style={styles.manageSubscriptionButton}>
            <Text style={styles.buttonText}>Manage Subscription</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.buttonContainer}>
          <View style={styles.subscriptionShadowBackground} />
          <TouchableOpacity onPress={handleReferFriend} style={styles.manageSubscriptionButton}>
            <Text style={styles.buttonText}>Refer a Friend</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Save Button */}
      <View style={styles.buttonContainer}>
        <View style={styles.saveShadowBackground} />
        <TouchableOpacity onPress={handleSave} style={styles.saveButton}>
          <Text style={styles.buttonText}>Save</Text>
        </TouchableOpacity>
      </View>

      <View style={{ alignItems: 'center', marginTop: 12 }}>
      <Text style={{ fontSize: 12, color: '#000' }}>
        Want to delete your account?{' '}
        <Text
          style={{ color: '#007aff', textDecorationLine: 'underline' }}
          onPress={() => {
            setParentalGateIntent('delete');
            setShowParentalGate(true);
          }}          
        >
          Tap here
        </Text>.
      </Text>
    </View>

      {/* Modal for Dropdown Options */}
      <Modal
        transparent={true}
        visible={modalVisible}
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setModalVisible(false)}>
          <View style={styles.modalOverlay} />
        </TouchableWithoutFeedback>
        <View style={styles.modalContent}>
        <FlatList
            data={voicesArray}
            keyExtractor={(item) => item} // 'item' is already inferred as a string
            renderItem={({ item }: { item: string }) => (
              <TouchableOpacity
                style={styles.modalOption}
                onPress={() => handleSelectVoice(item)}
              >
                <Text style={styles.modalOptionText}>{item}</Text>
              </TouchableOpacity>
            )}
          />
        </View>
      </Modal>
      <ParentalGateModal
        isVisible={showParentalGate}
        onSuccess={handleParentalGateSuccess}
        onCancel={handleParentalGateCancel}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#F7F8FB',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 32,
  },
  iconButton: {
    padding: 8,
  },
  icon: {
    width: 24,
    height: 24,
  },
  settingsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignSelf: 'center',
    maxWidth: 800,
    paddingTop: 75,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    width: CONTENT_WIDTH,
  },
  voiceSettingIcon: {
    width: 24,
    height: 24,
    marginRight: 16,
    alignSelf: 'center',
    marginBottom: 24,
  },
  settingIcon: {
    width: 24,
    height: 24,
    marginRight: 16,
    alignSelf: 'center',
  },
  dropdownContainer: {
    flex: 1,
    marginBottom: 20,
    backgroundColor: '#D9D9D9',
    borderRadius: 10,
    paddingHorizontal: 10,
  },
  dropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 15,
    backgroundColor: '#D9D9D9',
    borderRadius: 10,
    flex: 1, // Instead of maxWidth
    minWidth: 200,
    maxWidth: 500,
    minHeight: 60,
  },
  dropdownText: {
    fontSize: 16,
    color: '#000',
    textAlign: 'center',
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    position: 'absolute',
    bottom: 0,
    width: screenWidth,
    backgroundColor: '#FFF',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 16,
  },
  modalOption: {
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
  },
  modalOptionText: {
    fontSize: 16,
    color: '#000',
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: CONTENT_WIDTH,
    marginBottom: 50,
  },
  slider: {
    height: 50,
    width: CONTENT_WIDTH * 0.78,
    minWidth: 200,
    maxWidth: 500,
  },
  sliderValue: {
    fontSize: 16,
    marginLeft: 10,
  },
  buttonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    fontFamily: 'Futura',
  },
  buttonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  subscriptionShadowBackground: {
    position: 'absolute',
    width: CONTENT_WIDTH,
    height: 60,
    backgroundColor: '#AFC9BF',
    borderRadius: 32,
    top: '7%',
    left: '1%',
    zIndex: 0,
    alignSelf: 'center',
    maxWidth: 400,
  },  
  manageSubscriptionButton: {
    backgroundColor: '#544564',
    padding: 16,
    borderRadius: 32,
    width: CONTENT_WIDTH,
    maxWidth: 400,
    alignItems: 'center',
    zIndex: 1,
    alignSelf: 'center', // ADD THIS
  },  
  saveShadowBackground: {
    alignItems: 'center',
    width: '90%',
    height: 70,
    backgroundColor: '#AFC9BF',
    borderRadius: 16,
    maxWidth: 400,
    top: '65%',
    left: '.5%'
  },
  saveButton: {
    backgroundColor: '#544564',
    padding: 24,
    borderRadius: 16,
    marginTop: 32,
    width: '90%',
    alignItems: 'center',
    zIndex: 1,
    maxWidth: 400,
  },
});

export default SettingsPage;
